import React from "react";
import { summarise } from "../../shared/backup";

/**
 * The one question the app has to ask rather than answer.
 *
 * You signed in on a device that already had cards on it, and the account
 * already had different ones. Both were made by you, neither is a mistake, and
 * the dates cannot settle it — before this moment the two histories are
 * unrelated, so "newer" only says which browser you happened to open last.
 *
 * Shown with counts rather than key names, because the question is "which of
 * these is my collection", and nobody knows what ppt_collection_v1 is.
 */

const decode = (entries) => {
  const out = {};
  for (const [key, raw] of Object.entries(entries || {})) {
    try {
      out[key] = JSON.parse(raw);
    } catch {
      out[key] = raw;
    }
  }
  return out;
};

const describe = (entries) => {
  const lines = summarise(decode(entries));
  return lines.length ? lines : ["nothing"];
};

export default function SyncConflict({ conflict, onChoose, onClose, busy }) {
  if (!conflict) return null;
  const here = describe(conflict.local.entries);
  const there = describe(conflict.server.entries);

  return (
    <div className="sync-scrim" role="dialog" aria-modal="true" aria-label="Which collection to keep">
      <div className="sync-box">
        {/* The note below has always said to close this and take a backup
            first, and until now there was nothing to close it with. Deciding
            is not the only reasonable thing to want to do here. */}
        <button
          type="button"
          className="sync-close"
          onClick={onClose}
          disabled={busy}
          aria-label="Decide later"
        >
          ×
        </button>
        <h2 className="sync-title">Two collections</h2>
        <p className="sync-lede">
          This device and your account both have cards on them, and they don't
          match. Nothing has been changed yet — pick which one to keep.
        </p>

        <div className="sync-sides">
          <section className="sync-side">
            <h3>On this device</h3>
            <ul>{here.map((l) => <li key={l}>{l}</li>)}</ul>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy}
              onClick={() => onChoose("device")}
            >
              Keep this one
            </button>
          </section>

          <section className="sync-side">
            <h3>In your account</h3>
            <ul>{there.map((l) => <li key={l}>{l}</li>)}</ul>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={busy}
              onClick={() => onChoose("account")}
            >
              Keep this one
            </button>
          </section>
        </div>

        {/* Said plainly, because the honest answer to "can I have both" is no,
            and finding that out afterwards would be worse. */}
        <p className="sync-note">
          Whichever you keep replaces the other. If you want to hold on to both,
          close this, use <strong>Back up everything</strong> to save a copy
          first, then come back.
        </p>
        {/* What closing actually does, said before it is done rather than
            discovered afterwards. Nothing syncs until this is answered, and a
            person who does not know that will assume the opposite. */}
        <p className="sync-note">
          Closing changes nothing on either side — but your devices stay out of
          step until you pick.
        </p>
      </div>
    </div>
  );
}
